<template>
  <div class="reading-progress-view">
    <Header />

    <main class="reading-progress-main py-4">
      <div class="container-fluid px-3 px-md-4 px-lg-5 max-w-1440">

        <!-- HERO DASHBOARD BANNER — Refined Anti-Slop Editorial Design -->
        <section class="bookshelf-dashboard-hero mb-3">
          <div class="row align-items-center g-3 g-md-4 text-start">
            
            <!-- Left Column: Title & Intro -->
            <div class="col-lg-5 col-xl-5">
              <div class="hero-eyebrow mb-2">
                <span class="dot-accent"></span>
                <span>TỦ SÁCH CÁ NHÂN</span>
              </div>
              <h1 class="hero-display-title font-serif fw-bold text-dark mb-1">Tủ Sách Của Tôi</h1>
              <p class="hero-subtitle text-muted mb-0">
                Quản lý hành trình đọc sách, theo dõi tiến độ hàng ngày và lưu giữ những cảm nhận cá nhân tâm đắc.
              </p>
            </div>

            <!-- Right Column: Editorial Stat Metric Strip -->
            <div class="col-lg-7 col-xl-7">
              <div class="hero-stat-grid">
                <div class="stat-metric-card">
                  <div class="stat-header">
                    <span class="stat-label">Tất cả</span>
                    <icon icon="fa-solid fa-layer-group" class="stat-icon text-teal" />
                  </div>
                  <div class="stat-number text-dark">{{ totalCount }}</div>
                  <span class="stat-subtext">cuốn sách</span>
                </div>

                <div class="stat-metric-card">
                  <div class="stat-header">
                    <span class="stat-label">Đang đọc</span>
                    <span class="stat-dot bg-emerald"></span>
                  </div>
                  <div class="stat-number text-emerald">{{ readingCount }}</div>
                  <span class="stat-subtext">theo dõi</span>
                </div>

                <div class="stat-metric-card">
                  <div class="stat-header">
                    <span class="stat-label">Muốn đọc</span>
                    <span class="stat-dot bg-blue"></span>
                  </div>
                  <div class="stat-number text-blue">{{ wantCount }}</div>
                  <span class="stat-subtext">cuốn sách</span>
                </div>

                <div class="stat-metric-card">
                  <div class="stat-header">
                    <span class="stat-label">Đã xong</span>
                    <span class="stat-dot bg-purple"></span>
                  </div>
                  <div class="stat-number text-purple">{{ completedCount }}</div>
                  <span class="stat-subtext">hoàn thành</span>
                </div>

                <div class="stat-metric-card">
                  <div class="stat-header">
                    <span class="stat-label">Tạm ngưng</span>
                    <span class="stat-dot bg-amber"></span>
                  </div>
                  <div class="stat-number text-amber">{{ pausedCount }}</div>
                  <span class="stat-subtext">tạm dừng</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        <!-- TOP CONTROL BAR — Search, View Mode, Item Count -->
        <div class="top-control-bar-card mb-4" :class="{ 'is-stuck': isControlBarStuck }">
          <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
            
            <!-- Search & Mobile Filter Toolbar Group -->
            <div class="d-flex align-items-center gap-2 flex-grow-1 max-w-450 search-group-left">
              <div class="search-box-wrapper flex-grow-1">
                <icon icon="fa-solid fa-magnifying-glass" class="search-input-icon text-muted" />
                <input
                  v-model.trim="searchKeyword"
                  type="text"
                  class="search-progress-input"
                  placeholder="Nhập tên sách, tác giả..."
                />
                <button
                  v-if="searchKeyword"
                  type="button"
                  class="btn-clear-search border-0 bg-transparent p-0 position-absolute end-0 top-50 translate-middle-y me-3 text-muted"
                  @click="searchKeyword = ''"
                >
                  <icon icon="fa-solid fa-xmark" />
                </button>
              </div>

              <!-- Mobile/iPad Filter Dialog Button -->
              <button
                type="button"
                class="btn-mobile-filter-toggle d-lg-none flex-shrink-0"
                :class="{ 'active': activeFilterCount > 0 }"
                title="Mở bộ lọc sách"
                @click="showFilterModal = true"
              >
                <icon icon="fa-solid fa-sliders" />
                <span>Bộ lọc</span>
                <span v-if="activeFilterCount > 0" class="badge-filter-count">
                  {{ activeFilterCount }}
                </span>
              </button>
            </div>

            <!-- Right Controls: Count Badge + View Mode Switcher -->
            <div class="d-flex align-items-center gap-2 gap-md-3 ms-auto control-right-group">
              <div class="count-summary-badge">
                <icon icon="fa-solid fa-book-open-reader" class="count-icon" />
                <span>Hiển thị <strong class="count-highlight">{{ totalElements }}</strong> / {{ totalCount }} cuốn</span>
              </div>

              <div class="view-mode-switcher flex-shrink-0">
                <button
                  type="button"
                  :class="['btn btn-sm', viewMode === 'grid' ? 'btn-primary text-white' : 'btn-light text-muted']"
                  title="Hiển thị dạng Lưới"
                  @click="viewMode = 'grid'"
                >
                  <icon icon="fa-solid fa-border-all" />
                </button>
                <button
                  type="button"
                  :class="['btn btn-sm', viewMode === 'list' ? 'btn-primary text-white' : 'btn-light text-muted']"
                  title="Hiển thị dạng Danh sách"
                  @click="viewMode = 'list'"
                >
                  <icon icon="fa-solid fa-list-ul" />
                </button>
              </div>
            </div>

          </div>
        </div>

        <!-- MAIN 2-COLUMN LAYOUT -->
        <div class="row g-4 text-start">

          <!-- LEFT COLUMN: Book Cards Content -->
          <div class="col-12 col-lg-8 col-xl-8 col-xxl-9">

            <!-- LOADING -->
            <div v-if="isLoading" class="py-5 text-center">
              <div class="spinner-border text-primary spinner-border-sm" role="status">
                <span class="visually-hidden">Đang tải...</span>
              </div>
              <p class="text-muted mt-3 mb-0" style="font-size:0.88rem">Đang tải tủ sách của bạn...</p>
            </div>

            <!-- EMPTY STATE -->
            <div v-else-if="filteredBookNotes.length === 0" class="empty-progress-card text-center my-3">
              <div class="empty-icon-circle mb-3">
                <icon icon="fa-solid fa-book-open" class="text-muted" style="font-size:1.5rem" />
              </div>
              <h3 class="h5 font-serif fw-bold text-dark mb-2">Tủ sách hiện chưa có cuốn nào</h3>
              <p class="text-muted mx-auto mb-4" style="font-size:0.85rem;max-width:400px">
                <span v-if="searchKeyword">Không tìm thấy kết quả phù hợp với từ khóa "{{ searchKeyword }}".</span>
                <span v-else>Hãy cập nhật tiến độ đọc ở trang chi tiết sách để thêm vào tủ sách cá nhân.</span>
              </p>
              <router-link to="/" class="btn btn-primary rounded-pill px-4 py-2 fw-semibold shadow-xs" style="font-size:0.88rem">
                <icon icon="fa-solid fa-compass" class="me-1" />
                <span>Khám phá sách mới</span>
              </router-link>
            </div>

            <!-- MODE 1: GRID VIEW -->
            <div v-else-if="viewMode === 'grid'" class="bk-grid">
              <BookCard
                v-for="item in filteredBookNotes"
                :key="item.bookId"
                :item="item"
                mode="bookcase"
                @click="$router.push('/book/' + item.bookId)"
                @update="openEditModal"
                @delete="confirmDeleteBookcase"
                @view-note="openNoteView"
              />
            </div>


            <!-- MODE 2: LIST VIEW — Unbreakable High-End List Card -->
            <div v-else class="d-flex flex-column gap-3 text-start pt-2">
              <div
                v-for="item in filteredBookNotes"
                :key="'list-' + item.bookId"
                class="list-book-card d-flex flex-row align-items-start gap-3 p-3 p-md-4"
                @click="$router.push('/book/' + item.bookId)"
                style="cursor: pointer;"
              >
                <!-- Cover (left column on desktop) -->
                <div class="flex-shrink-0 pt-0">
                  <img
                    :src="item.cover || defaultCover"
                    :alt="item.bookName"
                    class="list-cover-img"
                    @error="onImageError"
                  />
                </div>

                <!-- Right: everything else stacked vertically -->
                <div class="d-flex flex-column flex-grow-1 min-w-0 gap-2">

                  <!-- Top row: Info + Progress & Actions -->
                  <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                    <!-- Info: badges, title, author/date -->
                    <div class="min-w-0">
                      <!-- Badges Row -->
                      <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <span :class="['bk-badge-status', getStatusBadgeClass(item.progressStatus)]">
                          <span class="dot-live"></span>
                          <span>{{ getStatusLabel(item.progressStatus) }}</span>
                        </span>
                        <span :class="['bk-own-tag', getOwnStatusBadgeClass(item.ownStatus)]" :title="'Sở hữu: ' + getOwnStatusLabel(item.ownStatus)">
                          <icon :icon="getOwnStatusIcon(item.ownStatus)" class="bk-own-icon" />
                          <span>{{ getOwnStatusLabel(item.ownStatus) }}</span>
                        </span>
                        <span v-if="item.myEvaluate > 0" class="refined-rating-chip" title="Đánh giá cá nhân của bạn">
                          <span>Bạn: {{ '★'.repeat(item.myEvaluate) }}</span>
                        </span>
                      </div>

                      <!-- Book Title -->
                      <h3 class="h6 font-serif fw-bold text-dark mb-1 truncate-2" :title="item.bookName">
                        <router-link :to="'/book/' + item.bookId" class="text-decoration-none text-dark hover-primary">
                          {{ item.bookName }}
                        </router-link>
                      </h3>

                      <!-- Author & Registered Date (Stacked) -->
                      <div class="text-muted mb-1 list-book-author-wrap" style="font-size:0.78rem" :title="item.authorsStr">
                        <icon icon="fa-solid fa-feather-pointed" class="text-accent opacity-75 me-1 flex-shrink-0" />
                        <template v-if="getAuthorsList(item).length > 0">
                          <template v-for="(author, idx) in getAuthorsList(item)" :key="author.id || author.name || idx">
                            <span class="list-book-author-name bk-tag-link" @click.stop="goSearchAuthor(author)">{{ author.name }}</span>
                            <span v-if="idx < getAuthorsList(item).length - 1">, </span>
                          </template>
                        </template>
                        <span v-else class="list-book-author-name bk-tag-link" @click.stop="goSearchAuthor(item.authorsStr)">{{ item.authorsStr || 'Chưa cập nhật tác giả' }}</span>
                      </div>
                      <div v-if="getTranslatorsStr(item)" class="text-muted mb-1 list-book-author-wrap" style="font-size:0.76rem" :title="getTranslatorsStr(item)">
                        <icon icon="fa-solid fa-language" class="text-accent opacity-75 me-1 flex-shrink-0" />
                        <span>{{ getTranslatorsStr(item) }}</span>
                      </div>
                      <div v-if="item.categoriesStr" class="text-muted mb-1" style="font-size:0.72rem">
                        <icon icon="fa-solid fa-tag" class="opacity-75 me-1" />
                        <template v-if="getCategoriesList(item).length > 0">
                          <template v-for="(cat, idx) in getCategoriesList(item)" :key="cat.id || cat.name || idx">
                            <span class="bk-categories-tag bk-tag-link" @click.stop="goSearchCategory(cat)">{{ cat.name }}</span>
                            <span v-if="idx < getCategoriesList(item).length - 1">, </span>
                          </template>
                        </template>
                        <span v-else class="bk-categories-tag bk-tag-link" @click.stop="goSearchCategory(item.categoriesStr)">{{ item.categoriesStr }}</span>
                      </div>
                      <div class="text-muted d-flex align-items-center gap-1 mb-0" style="font-size:0.78rem" title="Ngày thêm vào tủ sách">
                        <icon icon="fa-solid fa-clock-rotate-left" class="opacity-75 me-1" />
                        <span>Thêm: {{ formatDateTime(item.updateTime || item.createTime) }}</span>
                      </div>
                    </div>

                    <!-- Progress & Actions -->
                    <div class="list-right-section d-flex align-items-center justify-content-between gap-3 flex-shrink-0 ms-lg-auto pt-1">
                      <div class="list-progress-box">
                        <div class="d-flex justify-content-between align-items-center mb-1" style="font-size:0.75rem">
                          <span class="text-muted fw-semibold">Tiến độ</span>
                          <span :class="['text-nowrap', 'fw-bold', 'bk-progress-text', getProgressTextClass(item.progressStatus)]" style="font-size:0.72rem">{{ formatProgressText(item) }}</span>
                        </div>
                        <div class="progress refined-bar">
                          <div
                            class="progress-bar"
                            :class="getProgressBarClass(item.progressStatus)"
                            role="progressbar"
                            :style="{ width: calculateVisualPercent(item) + '%' }"
                          ></div>
                        </div>
                      </div>

                      <div class="list-action-cluster d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="bk-btn-update"
                          @click.stop="openEditModal(item)"
                        >
                          <icon icon="fa-solid fa-pen-to-square" />
                          <span>Cập nhật</span>
                        </button>
                        <button
                          type="button"
                          class="bk-btn-delete"
                          title="Xóa khỏi tủ sách"
                          @click.stop="confirmDeleteBookcase(item)"
                        >
                          <icon icon="fa-solid fa-trash-can" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Note (full width of right column, below info & progress) -->
                  <div v-if="isValidNote(item.note)" class="refined-note-box mt-0 w-100">
                    <div class="d-flex align-items-center gap-1 mb-1" style="font-size:0.74rem">
                      <icon icon="fa-solid fa-note-sticky" class="text-primary opacity-85" />
                      <strong class="text-dark">Note của bạn:</strong>
                    </div>
                    <p :class="['mb-0 text-secondary font-italic text-break', { 'note-collapsed': isLongNote(item.note) && !isNoteExpanded(item.bookId) }]" style="font-size:0.76rem;line-height:1.45">
                      "{{ item.note }}"
                    </p>
                    <button
                      v-if="isLongNote(item.note)"
                      type="button"
                      class="btn-note-expand-toggle"
                      @click.stop="toggleNoteExpand(item.bookId)"
                    >
                      <icon :icon="isNoteExpanded(item.bookId) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" />
                      <span>{{ isNoteExpanded(item.bookId) ? 'Thu gọn' : 'Xem thêm' }}</span>
                    </button>
                  </div>
                </div>

              </div>
            </div>

            <!-- Pagination Controls -->
            <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-4 mb-2" aria-label="Book notes navigation">
              <ul class="custom-pagination d-flex align-items-center gap-1 list-unstyled p-1.5 rounded-pill shadow-sm">
                <!-- Previous Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 0"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang trước"
                  >
                    <icon icon="fa-solid fa-chevron-left" />
                  </button>
                </li>

                <!-- Numeric page links -->
                <li
                  v-for="page in visiblePages"
                  :key="page"
                  class="page-item-custom"
                >
                  <!-- Ellipsis placeholder -->
                  <span v-if="page === '...'" class="page-link-ellipsis">...</span>
                  <!-- Page link button -->
                  <button
                    v-else
                    @click="goToPage(page)"
                    :class="['page-link-custom numeric-btn d-flex align-items-center justify-content-center', { 'active': currentPage === page }]"
                  >
                    {{ page + 1 }}
                  </button>
                </li>

                <!-- Next Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage === totalPages - 1"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang sau"
                  >
                    <icon icon="fa-solid fa-chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>

          </div>

          <!-- RIGHT COLUMN: Vertical Filter Sidebar (Desktop only) -->
          <div class="col-12 col-lg-4 col-xl-4 col-xxl-3 d-none d-lg-block">
            <div class="filter-sidebar-card sticky-top" style="top: 160px; z-index: 10;">
              
              <!-- Nhóm Status (Trạng thái đọc) -->
              <div class="filter-sidebar-group mb-4">
                <div class="filter-group-header mb-3 d-flex align-items-center gap-2 pb-2 border-bottom">
                  <icon icon="fa-solid fa-sliders" class="text-primary fs-8" />
                  <span class="fw-bold fs-7 text-dark">Trạng thái</span>
                </div>
                <div class="filter-checkbox-vertical d-flex flex-column gap-2">
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected('all') }]"
                    @click.prevent="toggleStatusFilter('all')"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isStatusSelected('all')" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Tất cả</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(1) }]"
                    @click.prevent="toggleStatusFilter(1)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isStatusSelected(1)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Muốn đọc</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(2) }]"
                    @click.prevent="toggleStatusFilter(2)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isStatusSelected(2)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Đang đọc</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(3) }]"
                    @click.prevent="toggleStatusFilter(3)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isStatusSelected(3)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Đã đọc xong</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(4) }]"
                    @click.prevent="toggleStatusFilter(4)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isStatusSelected(4)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Tạm ngưng</span>
                  </label>
                </div>
              </div>

              <!-- Nhóm Sở hữu (Định dạng sách) -->
              <div class="filter-sidebar-group">
                <div class="filter-group-header mb-3 d-flex align-items-center gap-2 pb-2 border-bottom">
                  <icon icon="fa-solid fa-boxes-stacked" class="text-primary fs-8" />
                  <span class="fw-bold fs-7 text-dark">Sở hữu</span>
                </div>
                <div class="filter-checkbox-vertical d-flex flex-column gap-2">
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected('all') }]"
                    @click.prevent="toggleOwnFilter('all')"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isOwnSelected('all')" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Tất cả</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(1) }]"
                    @click.prevent="toggleOwnFilter(1)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isOwnSelected(1)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Sở hữu sách giấy</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(2) }]"
                    @click.prevent="toggleOwnFilter(2)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isOwnSelected(2)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Sở hữu Ebook</span>
                  </label>
                  <label 
                    :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(0) }]"
                    @click.prevent="toggleOwnFilter(0)"
                  >
                    <span class="checkbox-square">
                      <icon v-if="isOwnSelected(0)" icon="fa-solid fa-check" class="check-icon" />
                    </span>
                    <span>Chưa sở hữu</span>
                  </label>
                </div>
              </div>

            </div>
          </div>
          <!-- END RIGHT COLUMN -->

        </div>
        <!-- END MAIN 2-COLUMN ROW -->

      </div>
      <!-- END CONTAINER -->
    </main>

    <!-- EDIT MODAL (Ghi chú & Tiến độ cá nhân — 100% khớp book-detail) -->
    <transition name="fade">
      <div v-if="showModal" class="custom-modal-backdrop" @click.self="closeModal">
        <div class="custom-modal-card animate-slide-up">
          <div class="custom-modal-header d-flex align-items-center justify-content-between">
            <h4 class="custom-modal-title font-serif fw-bold m-0">
              <icon icon="fa-solid fa-book-bookmark" class="me-2 text-primary-color" />
              Ghi chú & Tiến độ cá nhân
            </h4>
            <button @click="closeModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="custom-modal-body">
            <div v-if="isLoading" class="text-center py-5">
              <icon icon="fa-solid fa-spinner" class="fa-spin text-primary-color fs-3" />
              <p class="text-muted mt-2 fs-7">Đang tải dữ liệu ghi chú...</p>
            </div>

            <div v-else>
              <div class="row g-4 mb-4">
                <!-- Trạng thái đọc -->
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2 section-collapse-header" @click="toggleReadingStatus">
                    <icon v-if="isMobile" :icon="collapseReadingStatus ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-down'" class="section-collapse-icon" />
                    <label class="form-label fw-bold text-secondary fs-7 m-0">Trạng thái đọc</label>
                  </div>
                  <transition name="collapse-fade">
                    <div v-show="isMobile ? !collapseReadingStatus : true" class="status-options-grid">
                      <button 
                        type="button"
                        v-for="status in progressStatuses" 
                        :key="status.value"
                        :class="['btn-status-pill', 'status-' + status.value, { 'active': Number(modalForm.progressStatus) === Number(status.value) }]"
                        @click="selectProgressStatus(status.value)"
                      >
                        <icon :icon="status.icon" />
                        {{ status.label }}
                      </button>
                    </div>
                  </transition>
                </div>

                <!-- Trạng thái sở hữu -->
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 mb-2 section-collapse-header" @click="toggleOwnership">
                    <icon v-if="isMobile" :icon="collapseOwnership ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-down'" class="section-collapse-icon" />
                    <label class="form-label fw-bold text-secondary fs-7 m-0">Sở hữu sách</label>
                  </div>
                  <transition name="collapse-fade">
                    <div v-show="isMobile ? !collapseOwnership : true" class="status-options-grid">
                      <button 
                        type="button"
                        v-for="own in ownStatuses" 
                        :key="own.value"
                        :class="['btn-own-pill', { 'active': Number(modalForm.ownStatus) === Number(own.value) }]"
                        @click="modalForm.ownStatus = Number(own.value)"
                      >
                        <icon :icon="own.icon" />
                        {{ own.label }}
                      </button>
                    </div>
                  </transition>
                </div>
              </div>

              <!-- Tiến độ đọc (số trang / %) - Chỉ hiện khi Đang đọc (2) hoặc Tạm ngưng (4) -->
              <div v-if="Number(modalForm.progressStatus) === 2 || Number(modalForm.progressStatus) === 4" class="progress-edit-section border-top pt-4 mb-4">
                <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <label class="form-label fw-bold text-secondary fs-7 m-0">Tiến độ đọc</label>
                  
                  <!-- Toggle Type: Trang vs Phần trăm -->
                  <div class="progress-type-toggle">
                    <button 
                      type="button"
                      :class="['btn-toggle-type', { 'active': !modalForm.type }]"
                      @click="setNoteType(false)"
                    >
                      Theo số trang
                    </button>
                    <button 
                      type="button"
                      :class="['btn-toggle-type', { 'active': modalForm.type }]"
                      @click="setNoteType(true)"
                    >
                      Theo phần trăm (%)
                    </button>
                  </div>
                </div>

                <div class="row g-3 align-items-end">
                  <!-- Input số trang -->
                  <template v-if="!modalForm.type">
                    <div class="col-6 col-sm-4">
                      <label class="fs-8 text-muted mb-1 d-block">Trang hiện tại</label>
                      <input 
                        type="number" 
                        class="form-control progress-input-number" 
                        v-model.number="modalForm.currentPage"
                        min="0"
                        :max="modalForm.totalPage"
                        @input="calculatePercent"
                      />
                    </div>
                    <div class="col-6 col-sm-4">
                      <label class="fs-8 text-muted mb-1 d-block">Tổng số trang</label>
                      <input 
                        type="number" 
                        class="form-control progress-input-number" 
                        v-model.number="modalForm.totalPage"
                        min="0"
                        @input="calculatePercent"
                      />
                    </div>
                  </template>

                  <!-- Input % -->
                  <template v-else>
                    <div class="col-12 col-sm-8">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <label class="fs-8 text-muted">Phần trăm hoàn thành</label>
                      </div>
                      <div class="d-flex align-items-center gap-3">
                        <input 
                          type="range" 
                          class="form-range progress-slider flex-grow-1" 
                          v-model.number="modalForm.percent"
                          @input="onPercentInput"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <div class="percent-input-box">
                          <input 
                            type="number" 
                            class="percent-input-field" 
                            v-model.number="modalForm.percent"
                            @input="onPercentInput"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0"
                          />
                          <span class="percent-badge-unit">%</span>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Visual progress bar -->
                  <div class="col-12 mt-3">
                    <div class="custom-progress-track">
                      <div 
                        class="custom-progress-bar" 
                        :style="{ width: getModalVisualPercent() + '%' }"
                      ></div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mt-1.5 fs-8 text-muted">
                      <span>Tiến độ: <strong class="text-primary-color">{{ getModalVisualPercent() }}%</strong></span>
                      <span v-if="modalForm.totalPage > 0">{{ modalForm.currentPage }} / {{ modalForm.totalPage }} trang</span>
                    </div>
                  </div>

                  <!-- Suggest chuyển sang Đã đọc xong khi đọc hết trang -->
                  <div v-if="Number(modalForm.progressStatus) === 2 && !modalForm.type && modalForm.totalPage > 0 && modalForm.currentPage >= modalForm.totalPage" class="suggest-read-badge d-flex align-items-center gap-2 mt-3 mb-0">
                    <icon icon="fa-solid fa-circle-check" />
                    <span>Bạn đã đọc hết sách! Nhấn <strong @click="selectProgressStatus(3)">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                  </div>
                  <div v-if="Number(modalForm.progressStatus) === 2 && modalForm.type && modalForm.percent >= 100" class="suggest-read-badge d-flex align-items-center gap-2 mt-3 mb-0">
                    <icon icon="fa-solid fa-circle-check" />
                    <span>Bạn đã đọc hết sách! Nhấn <strong @click="selectProgressStatus(3)">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                  </div>
                </div>
              </div>

              <!-- Ghi chú cá nhân -->
              <div v-if="!showNoteTextarea" class="note-toggle-trigger text-center py-2 mb-4 border-top pt-4">
                <button type="button" class="btn-toggle-note-textarea" @click="openNoteTextarea">
                  <icon :icon="hasExistingNote ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" class="me-1.5" />
                  {{ hasExistingNote ? 'Xem / Chỉnh sửa ghi chú cá nhân' : 'Viết ghi chú cá nhân (tùy chọn)' }}
                </button>
              </div>

              <div v-else class="note-textarea-section border-top pt-4 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold text-secondary fs-7 m-0">
                    Ghi chú của bạn
                    <button type="button" class="btn-hide-note-textarea ms-2 text-muted" @click="hideNoteTextarea" title="Ẩn ghi chú">
                      (Ẩn đi)
                    </button>
                  </label>
                  <span class="fs-8 text-muted">{{ modalForm.note ? modalForm.note.length : 0 }} / 500 ký tự</span>
                </div>
                <textarea 
                  class="form-control note-textarea" 
                  rows="4" 
                  placeholder="Nhập ghi chú cá nhân của bạn tại đây (ví dụ: cảm nghĩ, trích dẫn tâm đắc, bài học rút ra từ cuốn sách...)"
                  v-model="modalForm.note"
                  maxlength="500"
                ></textarea>
              </div>

              <!-- Validation Alert helper -->
              <div v-if="isDeleteAction" class="alert alert-warning py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-triangle-exclamation" class="text-warning" />
                <span>Lưu ý: Chọn <strong>Chưa đọc</strong> và <strong>Chưa sở hữu</strong> sẽ xóa cuốn sách này khỏi tủ sách cá nhân, đồng thời xóa nhận xét và đánh giá của bạn (nếu có).</span>
              </div>
              <div v-else-if="hadPreviousBookData && (Number(modalForm.progressStatus) === 0 || Number(modalForm.progressStatus) === 1)" class="alert alert-info py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-circle-info" class="text-info" />
                <span>Lưu ý: Chọn <strong>{{ Number(modalForm.progressStatus) === 0 ? 'Chưa đọc' : 'Muốn đọc' }}</strong> sẽ tự động xóa nhận xét và đánh giá của bạn (nếu có).</span>
              </div>
              <div v-else-if="noteValidationError" class="alert alert-danger py-2 px-3 mb-3 fs-8 border-0 rounded-3 d-flex align-items-center gap-2">
                <icon icon="fa-solid fa-circle-exclamation" class="text-danger" />
                <span>{{ noteValidationError }}</span>
              </div>
            </div>
          </div>
          <div class="custom-modal-footer d-flex justify-content-end gap-2 border-top pt-3">
            <button type="button" class="btn-cancel-note" @click="closeModal" :disabled="isSaving">
              Hủy bỏ
            </button>
            <button 
              type="button" 
              :class="['btn-save-note', { 'btn-danger bg-danger text-white border-danger': isDeleteAction }]" 
              :disabled="!isNoteModified || isSaving || !!noteValidationError" 
              @click="saveBookNote"
            >
              <icon v-if="isSaving" icon="fa-solid fa-spinner" class="fa-spin" />
              <icon v-else-if="isDeleteAction" icon="fa-solid fa-trash-can" />
              <icon v-else icon="fa-solid fa-floppy-disk" />
              <span>{{ isSaving ? (isDeleteAction ? 'Đang xóa...' : 'Đang lưu...') : (isDeleteAction ? 'Xóa khỏi tủ sách' : 'Lưu ghi chú & tiến độ') }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- NOTE VIEW MODAL (Xem thêm ghi chú) -->
    <transition name="fade">
      <div v-if="showNoteViewModal" class="note-view-backdrop" @click.self="closeNoteView">
        <div class="note-view-card animate-scale-up">
          <div class="note-view-header">
            <div class="note-view-book" v-if="viewNoteItem">
              <img
                :src="viewNoteItem.cover || defaultCover"
                :alt="viewNoteItem.bookName"
                class="note-view-cover"
                @error="onImageError"
              />
              <div class="note-view-book-info">
                <h4 class="note-view-book-title font-serif fw-bold">{{ viewNoteItem.bookName }}</h4>
                <span class="note-view-book-author text-muted">{{ viewNoteItem.authorsStr || 'Chưa cập nhật' }}</span>
              </div>
            </div>
            <button type="button" class="note-view-close" @click="closeNoteView">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="note-view-body">
            <div class="note-view-label">
              <icon icon="fa-solid fa-note-sticky" />
              Ghi chú cá nhân của bạn
            </div>
            <p class="note-view-text">{{ viewNoteItem?.note }}</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- FILTER DIALOG MODAL (Mobile & iPad) -->
    <transition name="fade">
      <div v-if="showFilterModal" class="modal-backdrop-custom d-lg-none" @click.self="closeFilterModal">
        <div class="modal-card-custom filter-modal-dialog animate-scale-up text-start">
          
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
            <div class="d-flex align-items-center gap-2">
              <div class="stat-icon-badge bg-primary-soft">
                <icon icon="fa-solid fa-sliders" class="text-primary fs-8" />
              </div>
              <h3 class="h6 font-serif fw-bold text-dark mb-0">Bộ Lọc Tủ Sách</h3>
            </div>
            <button type="button" class="border-0 bg-transparent text-muted p-1" @click="closeFilterModal" style="font-size:1.25rem;line-height:1;cursor:pointer">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <!-- Body: Filter Groups -->
          <div class="modal-filter-body py-1">
            <!-- Nhóm Status Trạng thái -->
            <div class="filter-sidebar-group mb-4">
              <div class="filter-group-header mb-3 d-flex align-items-center gap-2 pb-2 border-bottom">
                <icon icon="fa-solid fa-sliders" class="text-primary fs-8" />
                <span class="fw-bold fs-8 text-dark text-uppercase tracking-wider">STATUS (TRẠNG THÁI ĐỌC)</span>
              </div>
              <div class="filter-checkbox-vertical d-flex flex-column gap-2">
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected('all') }]"
                  @click="toggleStatusFilter('all')"
                >
                  <span class="checkbox-square">
                    <icon v-if="isStatusSelected('all')" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Tất cả</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(1) }]"
                  @click="toggleStatusFilter(1)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isStatusSelected(1)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Muốn đọc</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(2) }]"
                  @click="toggleStatusFilter(2)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isStatusSelected(2)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Đang đọc</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(3) }]"
                  @click="toggleStatusFilter(3)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isStatusSelected(3)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Đã đọc xong</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isStatusSelected(4) }]"
                  @click="toggleStatusFilter(4)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isStatusSelected(4)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Tạm ngưng</span>
                </button>
              </div>
            </div>

            <!-- Nhóm Sở hữu (Định dạng sách) -->
            <div class="filter-sidebar-group mb-2">
              <div class="filter-group-header mb-3 d-flex align-items-center gap-2 pb-2 border-bottom">
                <icon icon="fa-solid fa-boxes-stacked" class="text-primary fs-8" />
                <span class="fw-bold fs-8 text-dark text-uppercase tracking-wider">SỞ HỮU (ĐỊNH DẠNG SÁCH)</span>
              </div>
              <div class="filter-checkbox-vertical d-flex flex-column gap-2">
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected('all') }]"
                  @click="toggleOwnFilter('all')"
                >
                  <span class="checkbox-square">
                    <icon v-if="isOwnSelected('all')" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Tất cả</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(1) }]"
                  @click="toggleOwnFilter(1)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isOwnSelected(1)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Sở hữu sách giấy</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(2) }]"
                  @click="toggleOwnFilter(2)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isOwnSelected(2)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Sở hữu Ebook</span>
                </button>
                <button 
                  type="button"
                  :class="['filter-checkbox-btn-vertical', { 'active': isOwnSelected(0) }]"
                  @click="toggleOwnFilter(0)"
                >
                  <span class="checkbox-square">
                    <icon v-if="isOwnSelected(0)" icon="fa-solid fa-check" class="check-icon" />
                  </span>
                  <span>Chưa sở hữu</span>
                </button>
              </div>
            </div>

          </div>

          <!-- Footer -->
          <div class="d-flex align-items-center justify-content-end gap-2 pt-3 border-top mt-3">
            <button
              type="button"
              class="btn btn-primary rounded-pill px-4 fw-semibold w-100 py-2"
              style="font-size:0.85rem"
              @click="closeFilterModal"
            >
              <span>Áp dụng & Đóng</span>
            </button>
          </div>

        </div>
      </div>
    </transition>

    <!-- Custom Delete Confirmation Modal -->
    <transition name="modal-fade">
      <div
        v-if="deleteConfirmModal.show"
        class="custom-modal-backdrop d-flex align-items-center justify-content-center"
        @click.self="closeDeleteModal"
      >
        <div class="delete-confirm-card animate-scale-up" role="dialog" aria-modal="true">
          <div class="delete-confirm-header">
            <div class="delete-icon-wrapper">
              <icon icon="fa-solid fa-trash-can" />
            </div>
            <h4 class="delete-confirm-title">Xác nhận xóa khỏi tủ sách</h4>
          </div>
          
          <div class="delete-confirm-body">
            <p class="delete-confirm-message text-start mb-3">
              Bạn đang yêu cầu xóa cuốn sách <strong>"{{ deleteConfirmModal.bookName }}"</strong> khỏi tủ sách cá nhân.
            </p>
            <p class="delete-confirm-message text-start text-danger fw-semibold">
              <icon icon="fa-solid fa-triangle-exclamation" class="me-1" />
              Lưu ý: Hành động này sẽ đồng thời xóa đánh giá sao và nhận xét (review) của bạn đối với cuốn sách này. Các bình luận và trả lời của bạn trong thảo luận sẽ không bị xóa. Thao tác này không thể hoàn tác!
            </p>
          </div>

          <div class="delete-confirm-actions">
            <button
              type="button"
              class="btn-delete-cancel"
              @click="closeDeleteModal"
              :disabled="deleteConfirmModal.isDeleting"
            >
              Hủy bỏ
            </button>
            <button
              type="button"
              class="btn-delete-confirm"
              @click="executeDelete"
              :disabled="deleteConfirmModal.isDeleting"
            >
              <icon v-if="deleteConfirmModal.isDeleting" icon="fa-solid fa-spinner" class="fa-spin me-1.5" />
              <icon v-else icon="fa-solid fa-trash-can" class="me-1.5" />
              <span>{{ deleteConfirmModal.isDeleting ? 'Đang xóa...' : 'Xác nhận xóa' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <Footer />
  </div>
</template>

<script src="./reading-progress.js"></script>
<style scoped src="./reading-progress.css"></style>
